import { apiClient } from '@/lib/api/client'

export type ClientAddressDetailsV2 = {
  street?: string | null
  number?: string | null
  box?: string | null
  postalCode?: string | null
  city?: string | null
  country?: string | null
} | null

export type ClientAddressV2 = {
  fullAddress?: string | null
  details?: ClientAddressDetailsV2
} | null

export type ClientContactV2 = {
  id?: string | null
  isPrimary?: boolean | null
  civility?: number | null
  firstName?: string | null
  lastName?: string | null
  phone?: string | null
  email?: string | null
  includeInCommunications?: boolean | null
}

export type ClientDetailsV2 = {
  customerId: string
  type?: number | null
  typeLabel?: string | null
  name?: string | null
  designation?: string | null
  vatNumber?: string | null
  companyRegistrationNumber?: string | null
  glnNumber?: string | null
  preferredLanguageId?: string | null
  preferredLanguageLabel?: string | null
  billingAddress?: ClientAddressV2
  contacts?: ClientContactV2[] | null
}

export type ClientSearchItemV2 = {
  customerId?: string | null
  type?: number | null
  name?: string | null
  vatNumber?: string | null
  companyRegistrationNumber?: string | null
  glnNumber?: string | null
  preferredLanguageId?: string | null
  contacts?: Array<{
    id?: string | null
    isPrimary?: boolean | null
    civility?: number | null
    firstName?: string | null
    lastName?: string | null
    phone?: string | null
    email?: string | null
  }> | null
  lastJobSites?: Array<{
    jobSiteId?: string | null
    id?: string | null
    reference?: string | null
    label?: string | null,
    address?: string | null,
    addressDetails?: {
      street?: string | null
      number?: string | null
      box?: string | null
      postalCode?: string | null
      city?: string | null
      country?: string | null
    } | null
  }> | null
}

export type ClientSearchResponseV2 = {
  total?: number | null
  items?: ClientSearchItemV2[] | null
  clients?: ClientSearchItemV2[] | null
  results?: ClientSearchItemV2[] | null
}

export async function searchClientsV2(params: {
  q: string
  skip?: number
  take?: number
  /** Also clients whose projects are all in the trash (hidden otherwise). Only the merge dialog sets it. */
  includeInactive?: boolean
}): Promise<ClientSearchItemV2[]> {
  const qs = new URLSearchParams()
  qs.set('q', params.q)
  if (params.skip != null) qs.set('skip', String(params.skip))
  if (params.take != null) qs.set('take', String(params.take))
  if (params.includeInactive) qs.set('includeInactive', 'true')

  const response = await apiClient.get<ClientSearchResponseV2 | ClientSearchItemV2[]>(
    `/api/v2/clients/search?${qs.toString()}`
  )

  if (Array.isArray(response)) return response
  return response.items || response.clients || response.results || []
}

export type ClientJobSiteItemV2 = {
  jobSiteId?: string | null
  id?: string | null
  label?: string | null
  address?: string | null
}

export type ClientJobSitesResponseV2 = {
  items?: ClientJobSiteItemV2[] | null
  jobSites?: ClientJobSiteItemV2[] | null
}

export async function getClientJobSitesV2(params: { customerId: string; take?: number }): Promise<ClientJobSiteItemV2[]> {
  const qs = new URLSearchParams()
  if (params.take != null) qs.set('take', String(params.take))

  const response = await apiClient.get<ClientJobSitesResponseV2 | ClientJobSiteItemV2[]>(
    `/api/v2/clients/${params.customerId}/job-sites${qs.toString() ? `?${qs.toString()}` : ''}`
  )

  if (Array.isArray(response)) return response
  return response.items || response.jobSites || []
}

export type ClientDuplicateCheckRequestV2 = {
  customerName?: string | null
  contactLastName?: string | null
  email?: string | null
  phone?: string | null
  vatNumber?: string | null
  companyRegistrationNumber?: string | null
  take: number
}

export type ClientDuplicateCheckCandidateV2 = {
  customerId?: string | null
  score?: number | null
  reasons?: string[] | null
  type?: number | null
  name?: string | null
  vatNumber?: string | null
  companyRegistrationNumber?: string | null
  preferredLanguageId?: string | null
  hasActiveProject?: boolean | null
  contacts?: Array<{
    id?: string | null
    isPrimary?: boolean | null
    civility?: number | null
    firstName?: string | null
    lastName?: string | null
    phone?: string | null
    email?: string | null
  }> | null
}

export type ClientDuplicateCheckResponseV2 = {
  items?: ClientDuplicateCheckCandidateV2[] | null
}

export async function checkClientDuplicateV2(body: ClientDuplicateCheckRequestV2): Promise<ClientDuplicateCheckResponseV2> {
  return await apiClient.post<ClientDuplicateCheckResponseV2>('/api/v2/clients/duplicate-check', body)
}

export async function getClientDetailsV2(customerId: string): Promise<ClientDetailsV2> {
  return await apiClient.get<ClientDetailsV2>(`/api/v2/clients/${customerId}`)
}

export type ClientJobSiteV2 = {
  id: string
  reference?: string | null
  label?: string | null
  address?: string | null
  addressDetails?: ClientAddressDetailsV2
}

export type ClientSalesOwnerV2 = {
  id: string
  fullName?: string | null
  initials?: string | null
} | null

export type ClientProjectInvoicingV2 = {
  amountInvoiced: number
  percentageInvoiced: number
  lastInvoiceDate?: string | null
} | null

export type ClientProjectInvoicingNodeV2 = {
  hasAccess: boolean
  errorCode?: string | null
  message?: string | null
  summary?: ClientProjectInvoicingV2
} | null

export type ClientProjectQuoteTotalsV2 = {
  totalBeforeTax?: number | null
  totalWithTax?: number | null
  salesOrderTotalWithTax?: number | null
} | null

export type ClientProjectQuoteNodeV2 = {
  hasAccess: boolean
  errorCode?: string | null
  message?: string | null
  isQuoteAccepted?: boolean
  totals?: ClientProjectQuoteTotalsV2
} | null

export type ClientProjectMilestoneV2 = {
  label?: string | null
  details?: string | null
  status?: string | null
}

export type ClientProjectItemV2 = {
  projectId: string
  reference?: string | null
  name?: string | null
  type?: string | null
  typeLabel?: string | null
  isDirectSale?: boolean
  createdAt?: string | null
  updatedAt?: string | null
  defaultVat?: number | null
  salesOwner?: ClientSalesOwnerV2
  quote?: ClientProjectQuoteNodeV2
  invoicing?: ClientProjectInvoicingNodeV2
  milestones?: ClientProjectMilestoneV2[] | null
}

export type ClientProjectGroupV2 = {
  jobSite?: ClientJobSiteV2 | null
  projects?: ClientProjectItemV2[] | null
}

export type ClientProjectsByJobSiteV2 = {
  groups?: ClientProjectGroupV2[] | null
}

export async function getClientProjectsByJobSiteV2(customerId: string): Promise<ClientProjectsByJobSiteV2> {
  return await apiClient.get<ClientProjectsByJobSiteV2>(`/api/v2/clients/${customerId}/projects`)
}

export type UpdateClientRequestV2 = {
  type: number
  name?: string | null
  designation?: string | null
  vatNumber?: string | null
  companyRegistrationNumber?: string | null
  glnNumber?: string | null
  preferredLanguageId?: string | null
  contacts?: Array<{
    id?: string | null
    isPrimary?: boolean | null
    civility?: number | null
    firstName?: string | null
    lastName?: string | null
    phone?: string | null
    email?: string | null
    includeInCommunications?: boolean | null
  }> | null
}

export async function updateClientV2(customerId: string, body: UpdateClientRequestV2): Promise<ClientDetailsV2> {
  return await apiClient.put<ClientDetailsV2>(`/api/v2/clients/${customerId}`, body)
}

export type UpdatePreferredLanguageResponseV2 = {
  preferredLanguageId: string
}

/**
 * Update only the customer's default language for FUTURE projects.
 * Does not cascade to existing projects — each keeps its own snapshot.
 */
export async function patchClientPreferredLanguageV2(
  customerId: string,
  preferredLanguageId: string
): Promise<UpdatePreferredLanguageResponseV2> {
  return await apiClient.patch<UpdatePreferredLanguageResponseV2>(
    `/api/v2/clients/${customerId}/preferred-language`,
    { preferredLanguageId }
  )
}

export type UpdateClientBillingAddressRequestV2 = {
  street?: string | null
  number?: string | null
  box?: string | null
  postalCode?: string | null
  city?: string | null
  country?: string | null
}

export async function updateClientBillingAddressV2(
  customerId: string,
  address: UpdateClientBillingAddressRequestV2
): Promise<void> {
  await apiClient.put(`/api/v2/clients/${customerId}/billing-address`, { address })
}

// SOLY-CUST-001 — merge a duplicate customer (the source) into the right one (the target).

export type CustomerMergeIdentityV2 = {
  id: string
  name: string
  /** 1 = individual, 2 = company. */
  type: number
  vatNumber?: string | null
}

export type CustomerMergePreviewV2 = {
  source: CustomerMergeIdentityV2
  target: CustomerMergeIdentityV2
  /** Projects moving to the target, trashed ones included. */
  projectCount: number
  deletedProjectCount: number
  quoteVersionCount: number
  invoiceCount: number
  creditNoteCount: number
  /** Invoices of the source that no credit note cancelled: the merge then needs an explicit acknowledgement. */
  uncreditedInvoiceCount: number
  contactsToCopyCount: number
  /** Source contacts already on the target (same e-mail or phone): deleted with the source. */
  contactsNotCopiedCount: number
  /** Different non-empty VAT numbers, or individual vs company: the merge then needs an acknowledgement. */
  identityDiffers: boolean
}

export type MergeClientRequestV2 = {
  targetId: string
  /** The user ticked « J'ai compris » (uncredited invoices and/or different identities). */
  acknowledgeWarnings: boolean
}

export async function getClientMergePreviewV2(sourceId: string, targetId: string): Promise<CustomerMergePreviewV2> {
  return await apiClient.get<CustomerMergePreviewV2>(
    `/api/v2/clients/${sourceId}/merge-preview?targetId=${encodeURIComponent(targetId)}`
  )
}

export async function mergeClientV2(sourceId: string, body: MergeClientRequestV2): Promise<{ targetId: string }> {
  return await apiClient.post<{ targetId: string }>(`/api/v2/clients/${sourceId}/merge`, body)
}
